import { createEntityAdapter, createSlice, Update } from "@reduxjs/toolkit"
import { PreferenceId, Priority, PriorityId } from "./types"
import {
  associatePriorityWithPreference,
  dataLoaded,
  dissociatePriorityWithPreference,
  removePreference,
} from "./actions"
import { createRestoreSnapshot, snapshotSlice } from "./snapshot"

const prioritiesAdapter = createEntityAdapter<Priority, PriorityId>({
  selectId: (priority) => priority.uuid,
})
const selectors = prioritiesAdapter.getSelectors()
const restoreSnapshot = createRestoreSnapshot(prioritiesAdapter)

export const prioritiesSlice = createSlice({
  name: "priorities",
  initialState: prioritiesAdapter.getInitialState({
    snapshot: prioritiesAdapter.getInitialState(),
  }),
  reducers: {},
  selectors: {
    selectPriority: selectors.selectById,
  },
  extraReducers(builder) {
    builder.addCase(dataLoaded, (state, action) => {
      prioritiesAdapter.removeAll(state)

      action.payload.forEach(({ preferences }) => {
        preferences.forEach(({ id, priorities, classDocument }) => {
          if (classDocument === null) return

          prioritiesAdapter.addMany(
            state,
            classDocument.timetablePriorities.map((priority) => ({
              ...priority,
              uuid: PriorityId({ id: priority.uuid }),
              preferenceId: null,
            })),
          )

          const updates: Update<Priority, PriorityId>[] = []

          priorities.forEach(({ priority }) => {
            const priorityId = PriorityId({ id: priority })
            const p = selectors.selectById(state, priorityId)
            if (p.preferenceId !== null) return
            updates.push({
              id: priorityId,
              changes: { preferenceId: PreferenceId({ id }) },
            })
          })

          prioritiesAdapter.updateMany(state, updates)
        })
      })
    })

    builder.addAsyncThunk(removePreference, {
      rejected: restoreSnapshot,
      pending: (state, action) => {
        snapshotSlice(state)
        prioritiesAdapter.updateMany(
          state,
          selectors
            .selectAll(state)
            .filter((p) => p.preferenceId === action.meta.arg.preference.id)
            .map((priority) => ({
              id: prioritiesAdapter.selectId(priority),
              changes: { preferenceId: null },
            })),
        )
      },
    })

    builder.addAsyncThunk(associatePriorityWithPreference, {
      rejected: restoreSnapshot,
      pending: (state, action) => {
        snapshotSlice(state)
        const { preference, priority } = action.meta.arg
        prioritiesAdapter.updateOne(state, {
          id: prioritiesAdapter.selectId(priority),
          changes: {
            preferenceId: PreferenceId({ id: preference.id }),
          },
        })
      },
    })

    builder.addAsyncThunk(dissociatePriorityWithPreference, {
      rejected: restoreSnapshot,
      pending: (state, action) => {
        snapshotSlice(state)
        const { priority } = action.meta.arg
        prioritiesAdapter.updateOne(state, {
          id: prioritiesAdapter.selectId(priority),
          changes: {
            preferenceId: null,
          },
        })
      },
    })
  },
})

const { selectPriority } = prioritiesSlice.selectors
export const prioritiesSelectors = {
  selectPriority,
}
